"use client";

import { useEffect, useRef } from "react";
import { formatAssumptionNum } from "./formatAssumptionNum";

export interface AssumptionHistoryEntry {
  at: string;
  actor: {
    type: "user" | "agent" | "system";
    userId: string | null;
    name: string | null;
    email: string | null;
  };
  oldValue: number | null;
  newValue: number | null;
  reason?: string;
}

export interface AssumptionHistoryDialogProps {
  label: string;
  unit?: string | null;
  format?: string | null;
  history: AssumptionHistoryEntry[];
  onClose: () => void;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled]):not([tabindex='-1'])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

function focusableElements(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

function formatValue(
  value: number | null,
  unit?: string | null,
  format?: string | null,
): string {
  if (value == null) return "—";
  return formatAssumptionNum(value, unit ?? null, format);
}

function actorLabel(entry: AssumptionHistoryEntry): string {
  if (entry.actor.name) return entry.actor.name;
  if (entry.actor.email) return entry.actor.email;
  if (entry.actor.type === "agent") return "Agent";
  if (entry.actor.type === "system") return "System";
  return "Unknown user";
}

export function AssumptionHistoryDialog({
  label,
  unit,
  format,
  history,
  onClose,
}: AssumptionHistoryDialogProps) {
  const dialogRef = useRef<HTMLElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const focusable = focusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      const active = document.activeElement;
      const leavingAtStart = event.shiftKey && active === first;
      const leavingAtEnd = !event.shiftKey && active === last;
      const focusIsOutside = !dialog.contains(active);

      if (leavingAtStart || leavingAtEnd || focusIsOutside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    (dialog ? focusableElements(dialog)[0] : null)?.focus();
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
      <button
        type="button"
        aria-label="Close history"
        tabIndex={-1}
        className="absolute inset-0 border-0 bg-black/30 cursor-default"
        onClick={onClose}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${label} history`}
        tabIndex={-1}
        className="relative flex max-h-[80vh] w-full max-w-[480px] flex-col overflow-hidden rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) shadow-(--shadow-lg)"
      >
        <header className="border-b border-(--border-subtle) px-5 py-4">
          <h2 className="m-0 text-sm font-bold text-(--fg-primary)">
            Change history
          </h2>
          <p className="mb-0 mt-1 text-[12.5px] text-(--fg-muted)">{label}</p>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2">
          {history.length === 0 ? (
            <p className="my-6 text-center text-[13px] text-(--fg-muted)">
              No changes recorded yet.
            </p>
          ) : (
            <ol className="m-0 list-none p-0">
              {history.map((entry) => (
                <li
                  key={`${entry.at}-${entry.oldValue}-${entry.newValue}`}
                  className="border-b border-(--border-subtle) py-3 last:border-b-0"
                >
                  <div className="flex items-center gap-2 text-[13px]">
                    <span className="text-(--fg-muted) line-through">
                      {formatValue(entry.oldValue, unit, format)}
                    </span>
                    <span className="text-(--fg-muted)">→</span>
                    <span className="font-semibold text-(--fg-primary)">
                      {formatValue(entry.newValue, unit, format)}
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-(--fg-secondary)">
                    <span>{actorLabel(entry)}</span>
                    <span aria-hidden="true"> · </span>
                    <time dateTime={entry.at}>
                      {new Date(entry.at).toLocaleString("en-US", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </time>
                  </div>
                  {entry.reason && (
                    <p className="mb-0 mt-2 rounded-(--radius-field) bg-(--bg-canvas) px-3 py-2 text-[12px] leading-[1.45] text-(--fg-secondary)">
                      {entry.reason}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
        <footer className="flex justify-end border-t border-(--border-subtle) px-5 py-3">
          <button
            type="button"
            className="inline-flex h-8 items-center justify-center rounded-(--radius-field) bg-(--brand-navy) px-3.5 text-sm font-medium text-(--fg-inverse) hover:bg-(--steel-110)"
            onClick={onClose}
          >
            Close
          </button>
        </footer>
      </section>
    </div>
  );
}
